import React from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/Badge/Badge";
import { formatCurrency } from "@/utils/formatCurrency";
import { getProductTypeBadgeConfig } from "../../utils/productTypeBadge";
import { ProductStoreOrderListItem } from "../../api/fetchProductStoreOrders/types";

interface OrderLineItemsExpandedRowProps {
  order: ProductStoreOrderListItem;
}

const OrderLineItemsExpandedRow: React.FC<OrderLineItemsExpandedRowProps> = ({
  order,
}) => {
  if (order.items.length === 0) {
    return (
      <div className="bg-white px-6 py-3 text-xs text-gray-500 dark:bg-slate-800 dark:text-slate-400">
        No items for this order.
      </div>
    );
  }

  return (
    <div className="bg-white px-6 py-3 dark:bg-slate-800">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wide text-gray-500 dark:text-slate-400">
            <th className="py-1 pr-4 font-medium">Product</th>
            <th className="py-1 pr-4 font-medium">Type</th>
            <th className="py-1 pr-4 font-medium">Category</th>
            <th className="py-1 pr-4 font-medium">Code</th>
            <th className="py-1 pr-4 font-medium">Qty</th>
            <th className="py-1 text-right font-medium">Unit Price</th>
          </tr>
        </thead>
        <tbody>
          {order.items.map((item, idx) => {
            const typeConfig = item.productType
              ? getProductTypeBadgeConfig(item.productType)
              : null;
            const TypeIcon = typeConfig?.icon;

            return (
              <tr
                className="border-t border-gray-200 text-gray-800 dark:border-slate-600 dark:text-slate-200"
                key={`${item.productCode}-${idx}`}
              >
                <td className="py-1.5 pr-4">
                  {item.productUuid ? (
                    <Link
                      className="text-blue-600 hover:underline dark:text-blue-400"
                      onClick={(e) => e.stopPropagation()}
                      to={`/product-directory/${item.productUuid}`}
                    >
                      {item.productName}
                    </Link>
                  ) : (
                    item.productName
                  )}
                </td>
                <td className="py-1.5 pr-4">
                  {typeConfig && TypeIcon ? (
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ${typeConfig.style}`}
                    >
                      <TypeIcon className="h-3 w-3" />
                      {typeConfig.label}
                    </span>
                  ) : (
                    "—"
                  )}
                </td>
                <td className="py-1.5 pr-4">
                  {item.category ? (
                    <Badge
                      className="whitespace-nowrap text-white"
                      style={{
                        backgroundColor:
                          item.category.color?.value || "#6B7280",
                      }}
                      variant="secondary"
                    >
                      {item.category.name}
                    </Badge>
                  ) : (
                    "—"
                  )}
                </td>
                <td className="py-1.5 pr-4 font-mono">
                  {item.productCode || "—"}
                </td>
                <td className="py-1.5 pr-4">{item.quantity}</td>
                <td className="py-1.5 text-right">
                  {formatCurrency(item.unitPrice)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default OrderLineItemsExpandedRow;
